import clsx from "clsx";
import { FC, Fragment, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import { SectionIcon } from "components/materials/section/SectionIcon";
import { FullSectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { Menu, type MenuChangeEvent, MenuItem } from "mds/components/Menu";
import { Tooltip } from "mds/components/Tooltip";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { ChevronRightIcon, CircleSuccessGreenIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectCurrentSectionId, selectFullSectionsSorted } from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { isLinkedSection } from "utils/publishing";
import { Link, useNavigate } from "utils/routing";
import { sectionHomeUrl, useViewLevel } from "utils/urls";
import { isCCMEnabled } from "utils/waffle-flags";

/**
 * A dropdown list of sections.
 */
export const SectionSelect: FC<{
  className?: string;
  disabled?: boolean;
  disabledTooltipText?: string;
  kind?: "primary" | "secondary";
  openInNewTab?: boolean;
}> = ({ className, disabled, disabledTooltipText, kind = "primary", openInNewTab }) => {
  const navigate = useNavigate();
  const currentSectionId = useAppSelector(selectCurrentSectionId) || "";
  const sections = useListSelector(selectFullSectionsSorted);
  // Note: we may still need to display an archived section if it's the current one, but for
  // the dropdown we filter them out.
  const { isActive } = useIsArchived();
  const viewLevel = useViewLevel();
  const isHomePage = viewLevel.home;
  const isSectionMainPage = viewLevel.section;
  const isSectionPage = viewLevel.section || viewLevel.page;
  const currentSection = sections.find((section) => section.id === currentSectionId);
  const currentSectionLabel = isHomePage
    ? t("main_header.select_section")
    : currentSection?.full_title;
  const ccmEnabled = isCCMEnabled();

  const groupedSections = useMemo(() => {
    const filteredSections = sections.filter(isActive);
    if (!ccmEnabled) {
      return filteredSections.map((section) => ({
        section,
        linkedSections: [] as FullSectionType[],
      }));
    }

    const sectionIds = new Set(filteredSections.map((section) => section.id));
    const linkedSectionsByTemplateId = new Map<string, FullSectionType[]>();
    for (const section of filteredSections) {
      if (isLinkedSection(section) && section.template_id && sectionIds.has(section.template_id)) {
        const list = linkedSectionsByTemplateId.get(section.template_id) ?? [];
        list.push(section);
        linkedSectionsByTemplateId.set(section.template_id, list);
      }
    }
    const grouped: { section: FullSectionType; linkedSections: FullSectionType[] }[] = [];
    for (const section of filteredSections) {
      if (
        !(isLinkedSection(section) && section.template_id && sectionIds.has(section.template_id))
      ) {
        grouped.push({
          section,
          linkedSections: linkedSectionsByTemplateId.get(section.id) ?? [],
        });
      }
    }
    return grouped;
  }, [sections, isActive, ccmEnabled]);

  const hasSectionsToSelect = groupedSections.length > 0;
  const isMobile = !useIsMdOrLarger();
  const [isMenuButtonHovered, setIsMenuButtonHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const buttonClasses = twMerge(
    "gap-1",
    kind === "secondary" && "!h-[32px] !max-h-[380px]",
    kind === "secondary" && !disabled && "!font-semibold",
    className,
  );

  const sectionTitleTooltipText = disabled ? disabledTooltipText : "";

  const MenuButton = (
    <div>
      <Tooltip content={sectionTitleTooltipText}>
        <Button
          className={buttonClasses}
          disabled={disabled}
          eventName="Open My Courses menu"
          kind="headerNav"
          size={kind === "primary" ? "s" : "m"}
          onMouseEnter={() => {
            setIsMenuButtonHovered(true);
          }}
          onMouseLeave={() => {
            setIsMenuButtonHovered(false);
          }}
        >
          {t("main_header.select_section")}
          <ChevronRightIcon
            className={clsx(
              "transition-transform duration-200 ease-in-out",
              (!isSectionPage || isMenuButtonHovered || isMenuOpen || kind === "secondary") &&
                "rotate-90",
            )}
          />
        </Button>
      </Tooltip>
    </div>
  );

  const sectionIcon = currentSection && <SectionIcon section={currentSection} />;

  return (
    <div className={clsx("flex min-w-0 gap-2", kind === "secondary" && "flex-col")}>
      {hasSectionsToSelect && (kind === "secondary" || !isMobile) && (
        <Menu
          align="start"
          menuButton={MenuButton}
          menuClassName="max-h-600"
          onMenuChange={(e: MenuChangeEvent) => {
            setIsMenuOpen(e.open);
          }}
        >
          {groupedSections.map(({ section, linkedSections }) => (
            <Fragment key={section.id}>
              <SectionMenuItem openInNewTab={openInNewTab} section={section} />
              {linkedSections.map((linked) => (
                <SectionMenuItem
                  className="ml-4"
                  key={linked.id}
                  openInNewTab={openInNewTab}
                  section={linked}
                />
              ))}
            </Fragment>
          ))}
        </Menu>
      )}
      {isSectionMainPage ? (
        <span className="flex h-[32px] min-w-0 flex-1 items-center gap-2 overflow-hidden p-2 text-sm font-medium text-black-tint-20 group-data-[dark]/themable:text-black-tint-70">
          {sectionIcon}
          <span className="block min-w-0 truncate" title={currentSectionLabel}>
            {currentSectionLabel}
          </span>
        </span>
      ) : (
        isSectionPage && (
          <div className="min-w-0 flex-1 overflow-hidden">
            <Tooltip content={sectionTitleTooltipText}>
              <Button
                className={twMerge(buttonClasses, "min-w-0 max-w-full gap-2")}
                disabled={disabled}
                eventName="Go to Course Overview"
                kind="headerNav"
                size={kind === "primary" ? "s" : "m"}
                onClick={() => {
                  navigate(sectionHomeUrl(currentSectionId));
                }}
              >
                {sectionIcon}
                <span className="block min-w-0 truncate" title={currentSectionLabel}>
                  {currentSectionLabel}
                </span>
              </Button>
            </Tooltip>
          </div>
        )
      )}
    </div>
  );
};

type SectionMenuItemProps = {
  className?: string;
  section: FullSectionType;
  openInNewTab?: boolean;
};

const SectionMenuItem: FC<SectionMenuItemProps> = ({ className, section, openInNewTab }) => {
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const checked = section.id === currentSectionId;
  const isMobile = !useIsMdOrLarger();
  const { canAuthorSection } = useCapabilities();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Link
      target={openInNewTab ? "_blank" : undefined}
      to={sectionHomeUrl(section.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <MenuItem
        checked={checked}
        className={clsx("flex items-center justify-between gap-8", className, {
          selected: checked,
        })}
        eventName="Section Selected"
        eventProps={{ sectionTitle: section.title }}
        value={section}
        allowPropagation
      >
        <div className="flex items-center gap-2">
          <SectionIcon
            className={checked && !isHovered ? "text-white" : undefined}
            section={section}
          />
          <div
            className={clsx("truncate", isMobile ? "max-w-xl" : "max-w-xxl")}
            title={section.full_title}
          >
            {section.full_title}
          </div>
        </div>

        {section.lms_section_id && canAuthorSection && (
          <div className="flex items-center gap-1" title={section.full_title}>
            <CircleSuccessGreenIcon />
            <span>{t("main_header.section_linked")}</span>
          </div>
        )}
      </MenuItem>
    </Link>
  );
};
